export type Path = `/${string}`;
export type ParsedPath = string[];
type ViewProvider =
	| HTMLElement
	| ((props: Record<string, string>) => HTMLElement)
	| string
	| { cmd: 'redirect'; path: Path }
	| (() => { cmd: 'redirect'; path: Path })
	| ((props: Record<string, string>) => string)
	| Router;

type ViewRenderer =
	| HTMLElement
	| ((props: Record<string, string>) => HTMLElement)
	| string
	| ((props: Record<string, string>) => string)
	| { cmd: 'redirect'; path: Path }
	| (() => { cmd: 'redirect'; path: Path })
	| SubRoute;

type ViewProvider2 = { props: Record<string, string>; renderer: ViewRenderer | undefined };

type SubRoute = { path: ParsedPath; router: Router };

type Route = { path: ParsedPath; viewProvider: ViewProvider } | { middleware: (next: () => void) => ViewRenderer | undefined };
type Router = {
	get: (path: Path, viewProvider: ViewProvider) => void;
	use: (middleware: (next: () => void) => ViewRenderer | undefined) => void;
	registeredRoutes: Route[];
	view: HTMLElement;
	parentRouter?: WeakRef<Router>;
	template?: HTMLElement;
};

export function parsePath(path: Path): ParsedPath {
	const parts = path.split('/').filter((x) => x);
	return parts;
}

export function makeRouter(): Router {
	const registeredRoutes: Route[] = [];

	const get = (path: Path, viewProvider: ViewProvider) => {
		if (typeof viewProvider === 'object' && 'registeredRoutes' in viewProvider) {
			viewProvider.parentRouter = new WeakRef(router);
		}

		registeredRoutes.push({
			path: parsePath(path),
			viewProvider,
		});
	};

	const use = (middleware: (next: () => void) => ViewRenderer | undefined) => {
		registeredRoutes.push({ middleware });
	};

	const router = { get, use, registeredRoutes, view: document.createElement('div') };

	return router;
}

export function matchRoute(route: ParsedPath, candidates: Route[]): ViewProvider2 {
	let remainingPath: ParsedPath = [];

	const props: Record<string, string> = {};

	for (const candidate of candidates) {
		let match = true;
		if ('middleware' in candidate) {
			let nextCalled = false;
			const nxt = () => (nextCalled = true);
			const res = candidate.middleware(nxt);

			if (res) {
				return { props, renderer: res };
			} else if (nextCalled) {
				continue;
			} else {
				break;
			}
		}

		for (let i = 0; i < Math.max(route.length, candidate.path.length); i++) {
			if (candidate.path[i] === undefined) {
				match = false;
				break;
			}

			if (candidate.path[i] === '*') {
				remainingPath = route.slice(i);
				match = true;
				break;
			}

			if (candidate.path[i] !== route[i] && candidate.path[i][0] !== ':') {
				match = false;
				break;
			}

			if (candidate.path[i][0] === ':') {
				const k = candidate.path[i].slice(1);
				props[k] = route[i];
			}
		}

		if (match) {
			if (typeof candidate.viewProvider === 'object' && 'registeredRoutes' in candidate.viewProvider) {
				return { props, renderer: { path: remainingPath, router: candidate.viewProvider } };
			} else {
				return { props, renderer: candidate.viewProvider };
			}
		}
	}

	return { props, renderer: undefined };
}

export function renderView(container: HTMLElement, viewProvider: ViewProvider2) {
	if (typeof viewProvider.renderer === 'undefined') {
		container.innerHTML = 'undefined';
	} else if (typeof viewProvider.renderer === 'string') {
		container.innerHTML = viewProvider.renderer;
	} else if (typeof viewProvider.renderer === 'function') {
		const props = viewProvider.props;
		renderView(container, { props, renderer: viewProvider.renderer(props) });
	} else if (viewProvider.renderer instanceof HTMLElement) {
		container.innerHTML = '';
		container.append(viewProvider.renderer);
	} else if (typeof viewProvider.renderer === 'object') {
		if ('cmd' in viewProvider.renderer) {
			const v = matchRoute(parsePath(viewProvider.renderer.path), _globalRouter.registeredRoutes);

			if (v) {
				history.replaceState(null, '', viewProvider.renderer.path);
				renderView(_globalRouter.view, v);
			}
		} else {
			if (viewProvider.renderer.router.template && container.childNodes[0] !== viewProvider.renderer.router.template) {
				renderView(container, { props: viewProvider.props, renderer: viewProvider.renderer.router.template });
			}

			const subViewProvider = matchRoute(viewProvider.renderer.path, viewProvider.renderer.router.registeredRoutes);
			renderView(viewProvider.renderer.router.view, {
				props: { ...viewProvider.props, ...subViewProvider.props },
				renderer: subViewProvider.renderer,
			});
		}
	} else {
		console.error('no idea what to do with this', viewProvider);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _globalRouter: Router = null as any;

export function mountRouter(root: HTMLElement, router: Router) {
	router.view = root;
	_globalRouter = router;

	root.addEventListener('click', (e) => {
		if (e.target && e.target instanceof HTMLElement) {
			const link = e.target.closest('[href]');
			if (link) {
				e.preventDefault();
				if (navigate(link.getAttribute('href') as Path)) {
					e.preventDefault();
					e.stopPropagation();
				}
			}
		}
	});

	const currentPath = parsePath(window.location.pathname as Path);
	const viewProvider = matchRoute(currentPath, router.registeredRoutes);

	window.dispatchEvent(new RouteChangeEvent(currentPath));

	renderView(root, viewProvider);
}

export function navigate(path: Path, pushState = true): boolean {
	const currentPath = parsePath(path);
	const viewProvider = matchRoute(currentPath, _globalRouter.registeredRoutes);
	if (viewProvider !== undefined) {
		if (pushState) history.pushState(null, '', path);

		window.dispatchEvent(new RouteChangeEvent(currentPath));
		renderView(_globalRouter.view, viewProvider);
		return true;
	} else {
		return false;
	}
}

export function reloadCurrentView() {
	const currentPath = parsePath(window.location.pathname as Path);
	const viewProvider = matchRoute(currentPath, _globalRouter.registeredRoutes);

	window.dispatchEvent(new RouteChangeEvent(currentPath));

	renderView(_globalRouter.view, viewProvider);
}

export function redirect(path: Path) {
	return { cmd: 'redirect' as const, path };
}

window.addEventListener('popstate', () => {
	// Handle back button press
	const p = window.location.pathname;
	navigate(p as Path, false);
});

export class RouteChangeEvent extends Event {
	constructor(public path: ParsedPath) {
		super('routechange');
	}
}
